'use client';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTheme, alpha } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { Logo } from 'src/components/logo';
import { SettingsButton } from '../components/settings-button';

export function AuthModernLayout({
  sx,
  children,
  backgroundImage = '/assets/background/login-bg.webp',
  overlayOpacity = 0.6,
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -2,
        }}
      />

      {/* Semi-transparent Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.darker || '#0a3d2e', overlayOpacity)} 0%, 
            ${alpha(theme.palette.grey[900], overlayOpacity + 0.2)} 50%,
            ${alpha('#000', overlayOpacity + 0.1)} 100%)`,
          zIndex: -1,
        }}
      />

      {/* Header with Logo */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 4 },
          py: 2,
          zIndex: 10,
        }}
      >
        <Logo
          sx={{
            '& img': {
              filter: 'brightness(0) invert(1)',
            },
          }}
        />

        <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
          <SettingsButton
            sx={{
              color: 'common.white',
              '&:hover': {
                bgcolor: alpha('#fff', 0.1),
              },
            }}
          />
        </Box>
      </Box>

      {/* Content Card */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 480,
          mx: 2,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px -12px ${alpha('#000', 0.25)}`,
          border: `1px solid ${alpha('#fff', 0.1)}`,
        }}
      >
        {children}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          py: 2,
          textAlign: 'center',
          color: alpha('#fff', 0.7),
          typography: 'caption',
        }}
      >
        © {new Date().getFullYear()} PickleLoop. All rights reserved.
      </Box>
    </Box>
  );
}
